import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Form, Message, Modal,
} from 'semantic-ui-react';

import ReasonFields, { reasonValidationError } from './ReasonFields';
import {
  DIALOG_CANCEL_LABEL,
  EDIT_STATUS_CONFIRM_LABEL,
  EDIT_STATUS_DIALOG_HEADER,
  EDIT_STATUS_OPTIONS,
  QUEUE_STATUS,
} from '../../constants/onboardingQueue';
import { toReasonSelection } from '../../utilities/onboardingQueue';

const EditStatusDialog = ({
  record, loading, errorMessage, onClose, onConfirm,
}) => {
  const stored = toReasonSelection(record.reason);
  const [status, setStatus] = useState(record.status);
  const [reason, setReason] = useState(stored.reason);
  const [otherText, setOtherText] = useState(stored.otherText);
  const [validationError, setValidationError] = useState('');

  const needsReason = status === QUEUE_STATUS.DECLINED;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleConfirm = () => {
    const invalid = needsReason ? reasonValidationError(reason, otherText) : '';
    setValidationError(invalid);
    if (invalid) return;
    onConfirm({ status, reason: needsReason ? reason : null, otherText: otherText.trim() });
  };

  return (
    <Modal
      open
      size='small'
      onClose={handleClose}
      closeOnEscape={!loading}
      closeOnDimmerClick={false}
      data-testid='queue-edit-status-dialog'
    >
      <Modal.Header>{EDIT_STATUS_DIALOG_HEADER}</Modal.Header>
      <Modal.Content>
        <p data-testid='queue-edit-status-dealer'>{record.dealerName}</p>

        <Form>
          <Form.Select
            required
            label='Status'
            options={EDIT_STATUS_OPTIONS}
            value={status}
            disabled={loading}
            data-testid='queue-edit-status-select'
            onChange={(event, { value }) => {
              setStatus(value);
              setValidationError('');
            }}
          />

          {needsReason && (
            <ReasonFields
              reason={reason}
              otherText={otherText}
              disabled={loading}
              invalid={Boolean(validationError)}
              onReasonChange={value => {
                setReason(value);
                setValidationError('');
              }}
              onOtherTextChange={value => {
                setOtherText(value);
                setValidationError('');
              }}
            />
          )}
        </Form>

        {validationError && (
          <Message negative size='mini' data-testid='queue-edit-status-validation'>
            <p>{validationError}</p>
          </Message>
        )}

        {errorMessage && (
          <Message negative data-testid='queue-edit-status-error'>
            <p>{errorMessage}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading} data-testid='queue-edit-status-cancel'>
          {DIALOG_CANCEL_LABEL}
        </Button>
        <Button
          primary
          loading={loading}
          disabled={loading}
          data-testid='queue-edit-status-confirm'
          onClick={handleConfirm}
        >
          {EDIT_STATUS_CONFIRM_LABEL}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

EditStatusDialog.propTypes = {
  record: PropTypes.shape({
    dealerName: PropTypes.string,
    status: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

EditStatusDialog.defaultProps = {
  loading: false,
  errorMessage: null,
};

export default EditStatusDialog;
